import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Calendar, TrendingUp, Award, Clock, Users, Target } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  nuovo: '#3B82F6',
  telefonata_target: '#F59E0B',
  videochiamata: '#6366F1',
  convertito: '#10B981',
  perso: '#6B7280'
};

export default function Reports() {
  const { token } = useAuth();
  const [period, setPeriod] = useState('week');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [period]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/reports?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento del report');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      nuovo: 'Nuovo',
      telefonata_target: 'Telefonata Target',
      videochiamata: 'Videochiamata',
      convertito: 'Convertito',
      perso: 'Perso'
    };
    return labels[status] || status;
  };

  const pieData = report?.leads_by_status
    ? Object.entries(report.leads_by_status).map(([status, count]) => ({
        name: getStatusLabel(status),
        value: count,
        color: STATUS_COLORS[status] || '#6B7280'
      }))
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-8">
        <p className="text-slate-500">Nessun dato disponibile</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2">Report Performance</h1>
          <p className="text-slate-600">Analisi dettagliata delle tue performance di vendita</p>
        </div>
        <div className="w-48">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-11" data-testid="period-filter">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Ultima Settimana</SelectItem>
              <SelectItem value="month">Ultimo Mese</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="bg-white border border-slate-200 p-6 shadow-sm" data-testid="total-leads-card">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-indigo-50 rounded-lg">
              <Users className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
          <p className="text-slate-500 text-sm font-medium uppercase tracking-wide mb-1">Lead Totali</p>
          <h3 className="text-3xl font-bold text-slate-900">{report.total_leads}</h3>
        </Card>

        <Card className="bg-white border border-slate-200 p-6 shadow-sm" data-testid="converted-card">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-emerald-50 rounded-lg">
              <Award className="h-6 w-6 text-emerald-600" />
            </div>
          </div>
          <p className="text-slate-500 text-sm font-medium uppercase tracking-wide mb-1">Convertiti</p>
          <h3 className="text-3xl font-bold text-slate-900">{report.converted_leads}</h3>
        </Card>

        <Card className="bg-white border border-slate-200 p-6 shadow-sm" data-testid="conversion-rate-card">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-amber-50 rounded-lg">
              <Target className="h-6 w-6 text-amber-600" />
            </div>
          </div>
          <p className="text-slate-500 text-sm font-medium uppercase tracking-wide mb-1">Tasso Conversione</p>
          <h3 className="text-3xl font-bold text-slate-900">{report.conversion_rate}%</h3>
        </Card>

        <Card className="bg-white border border-slate-200 p-6 shadow-sm" data-testid="avg-time-card">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <Clock className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <p className="text-slate-500 text-sm font-medium uppercase tracking-wide mb-1">Tempo Medio</p>
          <h3 className="text-3xl font-bold text-slate-900">
            {report.avg_conversion_time ? `${report.avg_conversion_time}h` : 'N/A'}
          </h3>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Daily Trend */}
        <Card className="bg-white border border-slate-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Andamento Giornaliero</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={report.daily_breakdown}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="day_label" stroke="#64748B" style={{ fontSize: '12px' }} />
              <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#FFF',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="total" name="Lead Totali" stroke="#4F46E5" strokeWidth={2} dot={{ fill: '#4F46E5' }} />
              <Line type="monotone" dataKey="converted" name="Convertiti" stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981' }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Status Distribution */}
        <Card className="bg-white border border-slate-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Distribuzione per Stato</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#FFF',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Daily Breakdown Table */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">Dettaglio Giornaliero</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Data</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Lead Totali</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Convertiti</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Tasso</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {report.daily_breakdown.map((day, index) => {
                const dayRate = day.total > 0 ? ((day.converted / day.total) * 100).toFixed(1) : '0.0';
                return (
                  <tr key={index} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{day.day_label}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{day.total}</td>
                    <td className="px-6 py-4 text-sm text-emerald-600 font-semibold">{day.converted}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{dayRate}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}